import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { TakePaymentDialog } from './TakePaymentDialog';
import { DollarSign, Plus, Undo2, RefreshCw, ShieldCheck, Receipt, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BookingPaymentsPanel — drops into a Booking-detail view.
 *
 * Props:
 *   bookingId — string
 */
export const BookingPaymentsPanel = ({ bookingId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargeForm, setChargeForm] = useState({ name: '', unit_price: '', quantity: 1, pin: '', notes: '' });
  const [savingCharge, setSavingCharge] = useState(false);
  const [refundFor, setRefundFor] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', pin: '', reason: '' });
  const [refunding, setRefunding] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/bookings/${bookingId}/payment-summary`, { withCredentials: true });
      setSummary(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load payment summary'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (bookingId) fetchSummary(); }, [bookingId]);

  const submitCharge = async () => {
    const { name, unit_price, quantity, pin } = chargeForm;
    if (!name.trim()) { toast.error('Charge name required'); return; }
    if (!(parseFloat(unit_price) > 0)) { toast.error('Unit price must be > 0'); return; }
    if (!/^\d{4,6}$/.test(pin)) { toast.error('Enter your 4-digit PIN'); return; }
    setSavingCharge(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/bookings/${bookingId}/extra-charge`,
        {
          name: chargeForm.name.trim(),
          unit_price: parseFloat(chargeForm.unit_price),
          quantity: parseInt(chargeForm.quantity, 10) || 1,
          notes: chargeForm.notes.trim(),
          pin: chargeForm.pin,
        },
        { withCredentials: true });
      toast.success(`Charge added — new total $${res.data.new_total.toFixed(2)}`);
      setChargeOpen(false);
      setChargeForm({ name: '', unit_price: '', quantity: 1, pin: '', notes: '' });
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add charge');
    } finally { setSavingCharge(false); }
  };

  const submitRefund = async () => {
    if (!refundFor) return;
    const amt = parseFloat(refundForm.amount);
    if (!(amt > 0)) { toast.error('Amount must be > 0'); return; }
    if (!/^\d{4,6}$/.test(refundForm.pin)) { toast.error('Enter your 4-digit PIN'); return; }
    setRefunding(true);
    try {
      await axios.post(`${API_URL}/api/admin/payments/${refundFor.payment_id}/refund`, {
        amount: amt, pin: refundForm.pin, reason: refundForm.reason,
      }, { withCredentials: true });
      toast.success('Refund recorded');
      setRefundFor(null);
      setRefundForm({ amount: '', pin: '', reason: '' });
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally { setRefunding(false); }
  };

  if (loading) return <div className="text-sm text-[#6B6B7A]" data-testid="bp-loading">Loading payments…</div>;
  if (!summary) return null;
  const paid = (summary.deposit_paid ? summary.deposit_amount : 0) + (summary.manual_paid_amount || 0);

  return (
    <div className="space-y-5" data-testid={`booking-payments-panel-${bookingId}`}>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center">
          <p className="text-xs text-[#6B6B7A]">Total</p>
          <p className="text-xl font-bold text-[#1E1E24]" data-testid="bp-total">${(summary.total || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center">
          <p className="text-xs text-[#6B6B7A]">Paid</p>
          <p className="text-xl font-bold text-[#06D6A0]" data-testid="bp-paid">${paid.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center">
          <p className="text-xs text-[#6B6B7A]">Outstanding</p>
          <p className="text-xl font-bold text-[#FF3B6B]" data-testid="bp-outstanding">${(summary.outstanding || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setPayOpen(true)} disabled={summary.outstanding <= 0.005}
          className="rounded-full bg-[#06D6A0] hover:bg-[#05b88a] text-white" data-testid="bp-take-payment">
          <Wallet className="w-4 h-4 mr-1" /> Take Payment
        </Button>
        <Button variant="outline" onClick={() => setChargeOpen(true)} className="rounded-full" data-testid="bp-add-charge">
          <Plus className="w-4 h-4 mr-1" /> Add Extra Charge
        </Button>
        <Button variant="outline" onClick={fetchSummary} className="rounded-full" data-testid="bp-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* History */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <div className="px-4 py-2 border-b border-[#E8E5DF] flex items-center justify-between">
          <h3 className="font-semibold text-sm text-[#1E1E24] flex items-center gap-1.5">
            <Receipt className="w-4 h-4" /> Payment history
          </h3>
          <span className="text-xs text-[#6B6B7A]">{summary.history.length} entries</span>
        </div>
        {summary.history.length === 0 ? (
          <p className="text-center text-sm text-[#6B6B7A] py-6">No payments yet for this booking.</p>
        ) : (
          <div className="divide-y divide-[#E8E5DF]" data-testid="bp-history">
            {summary.history.map((p) => (
              <div key={p.payment_id} className="px-4 py-3 flex items-center gap-3 text-sm" data-testid={`bp-history-row-${p.payment_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                      p.type === 'refund' ? 'bg-red-100 text-red-600' :
                      p.type === 'adjustment' ? 'bg-orange-100 text-orange-600' :
                      'bg-[#06D6A0]/15 text-[#058B69]'
                    }`}>{p.type}</span>
                    <span className="text-xs capitalize text-[#6B6B7A]">{(p.method || '').replace(/_/g, ' ')}</span>
                    <span className="text-[11px] text-[#6B6B7A]">{(p.created_at || '').replace('T', ' ').slice(0, 16)}</span>
                  </div>
                  {p.notes && <p className="text-xs text-[#6B6B7A] mt-0.5 truncate" title={p.notes}>{p.notes}</p>}
                  {p.employee_name && <p className="text-[11px] text-[#6B6B7A]">by {p.employee_name}</p>}
                </div>
                <span className={`font-bold ${p.amount < 0 ? 'text-[#FF3B6B]' : 'text-[#1E1E24]'}`}>${Number(p.amount).toFixed(2)}</span>
                {p.type === 'payment' && p.status === 'completed' && (
                  <Button size="sm" variant="ghost"
                    onClick={() => { setRefundFor(p); setRefundForm({ amount: Number(p.amount).toFixed(2), pin: '', reason: '' }); }}
                    className="text-[#FF3B6B] hover:bg-[#FFE4EB] h-7 px-2 text-xs"
                    data-testid={`bp-refund-${p.payment_id}`}
                  >
                    <Undo2 className="w-3 h-3 mr-1" /> Refund
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Take Payment dialog */}
      <TakePaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        booking={{
          booking_id: bookingId,
          customer_name: summary.customer_name,
          customer_email: summary.customer_email,
          total: summary.total,
          outstanding: summary.outstanding,
        }}
        onSuccess={fetchSummary}
      />

      {/* Add Extra Charge dialog */}
      <Dialog open={chargeOpen} onOpenChange={setChargeOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="bp-charge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4 text-[#FB923C]" />Add extra charge</DialogTitle>
            <DialogDescription>Adds a line item to the booking, recalculates total + tax. Re-opens the balance if it was already paid.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Charge name</Label>
              <Input value={chargeForm.name} onChange={(e) => setChargeForm({ ...chargeForm, name: e.target.value })}
                placeholder="e.g. Extra cleaning fee" className="mt-1" data-testid="bp-charge-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Unit price ($)</Label>
                <Input type="number" min="0" step="0.01" value={chargeForm.unit_price}
                  onChange={(e) => setChargeForm({ ...chargeForm, unit_price: e.target.value })} className="mt-1" data-testid="bp-charge-price" />
              </div>
              <div>
                <Label className="text-xs">Quantity</Label>
                <Input type="number" min="1" value={chargeForm.quantity}
                  onChange={(e) => setChargeForm({ ...chargeForm, quantity: e.target.value })} className="mt-1" data-testid="bp-charge-qty" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea rows={2} value={chargeForm.notes} onChange={(e) => setChargeForm({ ...chargeForm, notes: e.target.value })} className="mt-1" data-testid="bp-charge-notes" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Your 4-digit PIN</Label>
              <Input type="password" inputMode="numeric" maxLength={6} value={chargeForm.pin}
                onChange={(e) => setChargeForm({ ...chargeForm, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                className="mt-1 font-mono tracking-[0.4em] text-center" placeholder="••••" data-testid="bp-charge-pin" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChargeOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submitCharge} disabled={savingCharge}
              className="rounded-full bg-[#FB923C] hover:bg-[#F97316] text-white" data-testid="bp-charge-submit">
              {savingCharge ? 'Saving…' : 'Add charge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={!!refundFor} onOpenChange={(o) => !o && setRefundFor(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="bp-refund-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Undo2 className="w-4 h-4 text-[#FF3B6B]" />Refund payment</DialogTitle>
            <DialogDescription>
              Original payment: ${Number(refundFor?.amount || 0).toFixed(2)} ({(refundFor?.method || '').replace(/_/g, ' ')}).
              {refundFor?.method === 'credit_card_online' && ' Stripe will be called to issue the refund.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Refund amount ($)</Label>
              <Input type="number" min="0.01" step="0.01" value={refundForm.amount}
                onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} className="mt-1" data-testid="bp-refund-amount" />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea rows={2} value={refundForm.reason}
                onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} className="mt-1" data-testid="bp-refund-reason" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Your 4-digit PIN</Label>
              <Input type="password" inputMode="numeric" maxLength={6} value={refundForm.pin}
                onChange={(e) => setRefundForm({ ...refundForm, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                className="mt-1 font-mono tracking-[0.4em] text-center" placeholder="••••" data-testid="bp-refund-pin" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundFor(null)} className="rounded-full">Cancel</Button>
            <Button onClick={submitRefund} disabled={refunding}
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="bp-refund-submit">
              {refunding ? 'Refunding…' : 'Process refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingPaymentsPanel;
